import { create } from "zustand";
import { GeneralInformation } from "~/types/forms";
import { Partner } from "~/types/partners";
import { Compensation, Model, Scales } from "~/types/contract";
import { MappedProductsForClient } from "~/types/product";

import { setInitialDate } from "~/utils/date";

interface StepData {
    id: string;
    title: string;
    isPrepared: boolean;
    isCompleted: boolean;
}

interface ContractMilestones {
    product: {};
    brand: {};
    milestones: [];
}

interface FinancialConditions {
    conditions: {};
}

type Categories = {
    categoryName?: string;
    categoryId: string;
    categoryLabel: string;
    categoryValue: string;
};

interface CreateContractState {
    // General Information
    generalInformation: GeneralInformation;
    setGeneralInformation: (info: GeneralInformation) => void;

    // Contract Milestones
    contractMilestones: ContractMilestones;
    setContractMilestones: (milestones: any) => void;

    // Financial Conditions
    financialConditions: FinancialConditions;
    setFinancialConditions: (conditions: any) => void;

    // Contract Partners
    contractPartners: Partner[];
    setContractPartners: (partners: any) => void;

    // Products Details
    productsDetails: MappedProductsForClient;
    setProductsDetails: (products: any) => void;

    // Model Selection
    selectModel: Model;
    setSelectModel: (model: any) => void;

    // Category Tags
    selectCategories: Categories[];
    setSelectCategories: (categories: any) => void;

    // Steps
    performanceBasedSteps: StepData[];
    volumeBasedSteps: StepData[];
    setPerformanceBasedSteps: (steps: StepData[]) => void;
    setVolumeBasedSteps: (steps: StepData[]) => void;

    // Contract Review
    contractReview: {
        comments: string;
    };
    setContractReview: (review: any) => void;

    // Scales
    scales: Scales;
    setScales: (scales: Scales) => void;

    // Compensation
    contractCompensation: Compensation[];
    setContractCompensation: (compensations: Compensation[]) => void;

    // Reset functions
    resetGeneralInformation: () => void;
    resetProductsDetails: () => void;
    resetFinancialConditions: () => void;
    resetContractMilestones: () => void;
    resetContractPartners: () => void;

    // Reset all contract data
    resetAllContractData: () => void;
    resetSteps: () => void;
}

const defaultGeneralInformation: GeneralInformation = {
    reference: "",
    externalReference: "",
    icdNo: "",
    responsible: "",
    indication: {
        indicationId: "",
        indicationName: "",
    },
    therapeuticArea: {
        therapeuticAreaId: "",
        therapeuticAreaName: "",
    },
    country: { isoCode: "", name: "" },
    startDate: setInitialDate(),
    endDate: setInitialDate("plus", { years: 1 }),
    brand: {
        brandId: "",
        brandName: "",
    },
    primaryPartner: null,
    icdUrl: "",
    claimPeriodicity: { id: "MONTHLY", label: "Monthly" },
} as GeneralInformation;

const defaultContractMilestones = {
    product: {},
    brand: {},
    milestones: [],
};

const defaultPerformanceBasedSteps: StepData[] = [
    {
        id: "general-information",
        title: "General information",
        isPrepared: false,
        isCompleted: false,
    },
    {
        id: "product-details",
        title: "Product details",
        isPrepared: false,
        isCompleted: false,
    },
    {
        id: "financial-conditions",
        title: "Financial conditions",
        isPrepared: true,
        isCompleted: false,
    },
    {
        id: "contract-milestones",
        title: "Pricing & milestones",
        isPrepared: false,
        isCompleted: false,
    },
    {
        id: "additional-partners",
        title: "Additional partners",
        isPrepared: false,
        isCompleted: false,
    },
    {
        id: "contract-review",
        title: "Review & submit",
        isPrepared: true,
        isCompleted: false,
    },
];

const defaultVolumeBasedSteps: StepData[] = [
    {
        id: "model",
        title: "Model",
        isPrepared: false,
        isCompleted: false,
    },
    {
        id: "category-tags",
        title: "Category tags",
        isPrepared: false,
        isCompleted: false,
    },
    {
        id: "general-information",
        title: "General information",
        isPrepared: false,
        isCompleted: false,
    },
    {
        id: "product-details",
        title: "Product details",
        isPrepared: false,
        isCompleted: false,
    },
    {
        id: "additional-partners",
        title: "Additional partners",
        isPrepared: false,
        isCompleted: false,
    },
    {
        id: "scales",
        title: "Scales",
        isPrepared: false,
        isCompleted: false,
    },
    {
        id: "components",
        title: "Components",
        isPrepared: true,
        isCompleted: false,
    },
    {
        id: "contract-review",
        title: "Review & submit",
        isPrepared: true,
        isCompleted: false,
    },
];

export const useCreateContractStore = create<CreateContractState>(set => ({
    // General Information
    generalInformation: defaultGeneralInformation,
    setGeneralInformation: generalInformation => set({ generalInformation }),

    // Contract Milestones
    contractMilestones: defaultContractMilestones as ContractMilestones,

    setContractMilestones: contractMilestones => set({ contractMilestones }),

    // Financial Conditions
    financialConditions: {
        conditions: {},
    } as FinancialConditions,
    setFinancialConditions: financialConditions => set({ financialConditions }),

    // Contract Partners
    contractPartners: [] as Partner[],
    setContractPartners: contractPartners => set({ contractPartners }),

    // Contract Review
    contractReview: {
        comments: "",
    },
    setContractReview: contractReview => set({ contractReview }),

    // Products Details
    productsDetails: {
        products: [],
    } as MappedProductsForClient,
    setProductsDetails: productsDetails => set({ productsDetails }),

    // Model Selection
    selectModel: {
        datamodelId: "",
        name: "",
        priceSource: "",
        dataSource: "",
        allowedTotalUnitsTolerance: undefined,
        productContext: undefined,
        accountContext: undefined,
        regionContext: undefined,
        currency: "",
        type: "",
    } as Model,
    setSelectModel: selectModel => set({ selectModel }),

    // Category Tags
    selectCategories: [] as Categories[],
    setSelectCategories: selectCategories => set({ selectCategories }),

    // Scales
    scales: [] as Scales,
    setScales: (scales: Scales) => set({ scales }),
    //compensation
    contractCompensation: [] as Compensation[],
    setContractCompensation: (contractCompensation: Compensation[]) =>
        set({ contractCompensation }),
    setPerformanceBasedSteps: performanceBasedSteps =>
        set({
            performanceBasedSteps: performanceBasedSteps,
        }),
    setVolumeBasedSteps: volumeBasedSteps =>
        set({
            volumeBasedSteps: volumeBasedSteps,
        }),
    performanceBasedSteps: defaultPerformanceBasedSteps,
    volumeBasedSteps: defaultVolumeBasedSteps,
    // Reset functions
    resetGeneralInformation: () =>
        set({ generalInformation: defaultGeneralInformation }),
    resetProductsDetails: () =>
        set({
            productsDetails: { products: [] } as MappedProductsForClient,
        }),
    resetFinancialConditions: () =>
        set({
            financialConditions: { conditions: {} } as FinancialConditions,
        }),
    resetContractMilestones: () =>
        set({
            contractMilestones: defaultContractMilestones as ContractMilestones,
        }),
    resetContractPartners: () => set({ contractPartners: [] as Partner[] }),
    resetSteps: () =>
        set({
            performanceBasedSteps: [...defaultPerformanceBasedSteps],
            volumeBasedSteps: [...defaultVolumeBasedSteps],
        }),
    // Reset all contract data
    resetAllContractData: () =>
        set({
            generalInformation: { ...defaultGeneralInformation },
            contractMilestones: {
                ...defaultContractMilestones,
            } as ContractMilestones,
            financialConditions: { conditions: {} } as FinancialConditions,
            contractPartners: [] as Partner[],
            contractReview: { comments: "" },
            productsDetails: { products: [] } as MappedProductsForClient,
            selectModel: {
                datamodelId: "",
                name: "",
                priceSource: "",
                dataSource: "",
                allowedTotalUnitsTolerance: undefined,
                productContext: undefined,
                accountContext: undefined,
                regionContext: undefined,
                currency: "",
                type: "",
            } as Model,
            selectCategories: [] as Categories[],
            scales: [] as Scales,
            contractCompensation: [] as Compensation[],
            performanceBasedSteps: [...defaultPerformanceBasedSteps],
            volumeBasedSteps: [...defaultVolumeBasedSteps],
        }),
}));
